<aside class="gp" [class.open]="guide.open" aria-label="Guide" [attr.aria-hidden]="!guide.open" [attr.inert]="guide.open ? null : ''">
    <header class="gp-head">
        <div class="gp-title">
            <span class="gp-face"><i class="fas fa-lightbulb"></i></span>
            <div class="gp-title-copy">
                <h2>Guide</h2>
                <small>{{ title }}@if (where && where !== title) { · {{ where }} }</small>
            </div>
            <button type="button" class="gp-x" (click)="guide.close()" aria-label="Close the Guide" title="Close (Esc)">
                <i class="fas fa-xmark"></i>
            </button>
        </div>
        <nav class="gp-tabs" role="tablist">
            @for (tab of tabs; track tab.id) {
                <button type="button" role="tab" class="gp-tab" [class.on]="guide.tab === tab.id"
                        [attr.aria-selected]="guide.tab === tab.id" (click)="setTab(tab.id)">
                    <i class="fas" [ngClass]="tab.icon"></i>{{ tab.label }}
                </button>
            }
        </nav>
    </header>

    <div class="gp-body">
        <!-- A walkthrough rides on every tab: it is the job in hand. -->
        @if (guide.workflow; as active) {
            <section class="gp-wf" aria-label="Walkthrough in progress">
                <div class="gp-wf-top">
                    <i class="fas fa-flag"></i>
                    <span>{{ active.workflow.title }} · step {{ active.step + 1 }} of {{ active.workflow.steps.length }}</span>
                    <button type="button" (click)="guide.stopWorkflow()">Stop</button>
                </div>
                <div class="gp-bar"><i [style.width.%]="(active.step / active.workflow.steps.length) * 100"></i></div>
                <div class="gp-wf-now" [innerHTML]="line(active.workflow.steps[active.step].text)"></div>
                @if (active.workflow.steps[active.step].topic; as stepTopic) {
                    @if (stepTopicTitle(stepTopic)) {
                        <small class="gp-wf-where">On: {{ stepTopicTitle(stepTopic) }}</small>
                    }
                }
                <div class="gp-wf-nav">
                    @if (!guide.workflowStepHere && guide.workflowStepHasScreen) {
                        <button type="button" class="gp-btn primary" (click)="guide.goToWorkflowStep()">Take me there</button>
                    }
                    @if (active.step > 0) {
                        <button type="button" class="gp-btn" (click)="guide.previousWorkflowStep()">Back</button>
                    }
                    <button type="button" class="gp-btn" (click)="guide.nextWorkflowStep()">
                        {{ active.step === active.workflow.steps.length - 1 ? 'Finish' : 'Done, next step' }}
                    </button>
                </div>
            </section>
        }

        @switch (guide.tab) {
            @case ('screen') {
                @if (ref; as r) {
                    <div class="gp-lead" [innerHTML]="md(r.topic.summary)"></div>
                    @if (r.topic.audience) {
                        <div class="gp-meta"><span class="gp-chip"><i class="fas fa-user-group"></i>{{ r.topic.audience }}</span></div>
                    }
                    @if (r.topic.reach) {
                        <p class="gp-reach"><b>How to get here: </b><span [innerHTML]="line(r.topic.reach)"></span></p>
                    }
                    @if (r.topic.tour?.length) {
                        <button type="button" class="gp-tour" (click)="guide.startTour(r)">
                            <span class="gp-play"><i class="fas fa-play"></i></span>
                            <span><b>Show me around this screen</b><small>{{ r.topic.tour!.length }} stops · about a minute</small></span>
                        </button>
                    }

                    @if (r.topic.tasks.length) {
                        <h3 class="gp-h">Things you can do here</h3>
                        @for (task of r.topic.tasks; track $index; let ti = $index) {
                            <div class="gp-task" [class.open]="openTask === ti">
                                <button type="button" class="gp-task-head" (click)="toggleTask(ti)" [attr.aria-expanded]="openTask === ti">
                                    <i class="fas fa-chevron-right gp-chev"></i>
                                    <span>{{ task.title }}</span>
                                    <small>
                                        @if (tickedCount(ti)) { {{ tickedCount(ti) }}/{{ task.steps.length }} done } @else { {{ task.steps.length }} steps }
                                    </small>
                                </button>
                                @if (openTask === ti) {
                                    <div class="gp-steps">
                                        @if (task.when) { <p class="gp-when">{{ task.when }}</p> }
                                        @for (step of task.steps; track $index; let si = $index) {
                                            <div class="gp-step" role="checkbox" tabindex="0"
                                                 [class.done]="isTicked(ti, si)" [class.next]="nextStep(task, ti) === si"
                                                 [attr.aria-checked]="isTicked(ti, si)"
                                                 (click)="toggleStep(ti, si)"
                                                 (keydown.space)="$event.preventDefault(); toggleStep(ti, si)"
                                                 (keydown.enter)="toggleStep(ti, si)">
                                                <span class="gp-box">@if (isTicked(ti, si)) { <i class="fas fa-check"></i> } @else { {{ si + 1 }} }</span>
                                                <span [innerHTML]="line(step)"></span>
                                            </div>
                                        }
                                        @if (task.result && tickedCount(ti) === task.steps.length) {
                                            <div class="gp-result"><b>Done. </b><span [innerHTML]="line(task.result)"></span></div>
                                        } @else if (task.result) {
                                            <p class="gp-expect"><b>You should see: </b><span [innerHTML]="line(task.result)"></span></p>
                                        }
                                        @if (tickedCount(ti)) {
                                            <button type="button" class="gp-link" (click)="resetTask(ti, $event)">Start these steps again</button>
                                        }
                                    </div>
                                }
                            </div>
                        }
                    }

                    @for (caution of r.topic.cautions || []; track $index) {
                        <div class="gp-caution" [innerHTML]="line(caution)"></div>
                    }

                    @if (r.topic.fields?.length) {
                        <details class="gp-more">
                            <summary>What you see on this screen</summary>
                            <dl>
                                @for (field of r.topic.fields; track $index) {
                                    <dt [innerHTML]="line(field.term)"></dt>
                                    <dd [innerHTML]="line(field.meaning)"></dd>
                                }
                            </dl>
                        </details>
                    }
                    @if (r.topic.errors?.length) {
                        <details class="gp-more">
                            <summary>Messages you may see</summary>
                            <dl>
                                @for (error of r.topic.errors; track $index) {
                                    <dt>@if (error.code) { <code class="gp-code">{{ error.code }}</code> }<span [innerHTML]="line(error.message)"></span></dt>
                                    <dd><span [innerHTML]="line(error.meaning)"></span><b> What to do: </b><span [innerHTML]="line(error.fix)"></span></dd>
                                }
                            </dl>
                        </details>
                    }
                    @if (r.topic.tips?.length) {
                        <details class="gp-more">
                            <summary>Tips</summary>
                            <ul>@for (tip of r.topic.tips; track $index) { <li [innerHTML]="line(tip)"></li> }</ul>
                        </details>
                    }
                    @if (r.topic.faqs?.length) {
                        <h3 class="gp-h">People often ask</h3>
                        @for (faq of r.topic.faqs; track $index) {
                            <details class="gp-faq">
                                <summary>{{ faq.q }}</summary>
                                <div [innerHTML]="md(faq.a)"></div>
                            </details>
                        }
                    }
                    @if (r.topic.access) {
                        <details class="gp-more">
                            <summary>Who can use this screen</summary>
                            <div [innerHTML]="md(r.topic.access)"></div>
                        </details>
                    }

                    @if (workflowsHere.length && !guide.workflow) {
                        <h3 class="gp-h">Walkthroughs that use this screen</h3>
                        @for (item of workflowsHere; track item.workflow.id) {
                            <button type="button" class="gp-card" (click)="guide.startWorkflow(item.workflow.id)">
                                <b>{{ item.workflow.title }}</b>
                                <small>Step {{ item.step + 1 }} of {{ item.workflow.steps.length }} is here · {{ item.workflow.goal }}</small>
                                <span class="gp-card-go">Start <i class="fas fa-arrow-right"></i></span>
                            </button>
                        }
                    }

                    @if (r.topic.related?.length) {
                        <h3 class="gp-h">Related screens</h3>
                        <div class="gp-related">
                            @for (id of r.topic.related; track id) {
                                @if (related(id); as rel) {
                                    <button type="button" (click)="guide.openTopic(rel)">{{ rel.topic.title }}</button>
                                }
                            }
                        </div>
                    }
                    <a class="gp-link gp-centre" [routerLink]="helpCentreLink().url" [queryParams]="helpCentreLink().query">
                        Read this page in the Help centre <i class="fas fa-arrow-right"></i>
                    </a>
                } @else if (!guide.manualReady && !guide.manualFailed) {
                    <div class="gp-empty"><i class="fas fa-spinner fa-spin"></i><p>Opening the manual…</p></div>
                } @else if (guide.manualFailed) {
                    <div class="gp-empty">
                        <i class="fas fa-triangle-exclamation"></i>
                        <p>The manual could not be loaded.</p>
                        <div class="gp-empty-actions"><button type="button" class="gp-btn" (click)="guide.ensureManual()">Try again</button></div>
                    </div>
                } @else {
                    <div class="gp-empty">
                        <i class="fas fa-book-open-reader"></i>
                        <p>There is no page in the manual for <b>{{ guide.screenLabel || 'this screen' }}</b> yet.</p>
                        <p>Search the manual, or take the tour of Spectrum.</p>
                        <div class="gp-empty-actions">
                            <button type="button" class="gp-btn primary" (click)="setTab('manual')">Search the manual</button>
                            <button type="button" class="gp-btn" (click)="guide.startWelcomeTour()">Tour of Spectrum</button>
                        </div>
                    </div>
                }
            }

            @case ('manual') {
                <label class="gp-search">
                    <i class="fas fa-magnifying-glass"></i>
                    <input type="search" [(ngModel)]="search" placeholder="Search the manual…" aria-label="Search the manual" />
                </label>
                @if (search.trim().length > 1) {
                    <div class="gp-hits">
                        @for (hit of hits; track hit.ref.topic.id) {
                            <button type="button" class="gp-hit" (click)="openHit(hit)">
                                <b [innerHTML]="line(hit.label)"></b>
                                <small>{{ hit.ref.topic.title }}@if (hit.ref.manual.title !== hit.ref.topic.title) { · {{ hit.ref.manual.title }} }</small>
                            </button>
                        } @empty {
                            <p class="gp-fine">Nothing in the manual matches. Try other words — the name of a button, a column, or a message you saw.</p>
                        }
                    </div>
                } @else {
                    @if (manualWorkflows.length) {
                        <h3 class="gp-h">Walkthroughs</h3>
                        @for (w of manualWorkflows; track w.id) {
                            <button type="button" class="gp-card" (click)="guide.startWorkflow(w.id)">
                                <b>{{ w.title }}</b>
                                <small>{{ w.steps.length }} steps@if (w.cadence) { · {{ w.cadence }} }</small>
                                <span class="gp-card-go">Start <i class="fas fa-arrow-right"></i></span>
                            </button>
                        }
                    }
                    @for (chapter of chapters; track chapter.id) {
                        <h3 class="gp-h">{{ chapter.title }}</h3>
                        <div class="gp-index">
                            @for (item of chapter.topics; track item.topic.id) {
                                <button type="button" [class.here]="item.topic.id === guide.current?.topic?.id" (click)="guide.openTopic(item)">
                                    {{ item.topic.title }}
                                </button>
                            }
                        </div>
                    }
                    @if (!guide.manualReady && !guide.manualFailed) {
                        <div class="gp-empty"><i class="fas fa-spinner fa-spin"></i><p>Opening the manual…</p></div>
                    }
                }
                <div class="gp-foot-actions">
                    <a class="gp-btn" routerLink="/help">Open the Help centre</a>
                    <button type="button" class="gp-btn" (click)="guide.startWelcomeTour()">Tour of Spectrum</button>
                </div>
            }
        }
    </div>
</aside>
